/**
 * The documents browser (#6559): the chips row and the listing — table,
 * grid or Home — over the documents search endpoint.
 *
 * One component for two surfaces. The Documents page mounts it in `page` mode
 * in the Drive model: the page owns the destinations column, the topbar
 * search and the title row (passed in as `header`), and the browser lays the
 * chips and the listing beneath. The shared file-selection modal mounts it in
 * `picker` mode with its own top row — the chip-style view toggles and an
 * inline search — and a selection it shares with the tree. What the two must
 * never do is drift, which is why the picker is a mode and not a copy.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Popover } from "@mui/material";
import {
  CaretDownIcon,
  CaretRightIcon,
  CheckIcon,
  GlobeIcon,
} from "@phosphor-icons/react";
import { QuietScrollStrip } from "@/components/common/QuietScrollStrip";
import { useFrameWidth } from "@/hooks/useFrameWidth";
import openFile from "@/components/FpAppV4/services/openFile";
import DocumentsHome from "../DocumentsHome";
import Breadcrumbs from "./Breadcrumbs";
import DocumentsTable, {
  DocumentsTableSelection,
  rowSelectable,
} from "./DocumentsTable";
import FilterChips, {
  chipPopoverSlotProps,
  menuItemClass,
  type LocationChipProps,
  type SourceGate,
} from "./FilterChips";
import type { FolderAction } from "./RowMenu";
import SearchField from "./SearchField";
import type { DocumentRow, LinkedEntity, RecordSummary } from "./api";
import type { FolderEntry } from "@/hooks/useFileTreeV2/fetchFiles";
import {
  BrowserSort,
  BrowserState,
  BrowserView,
  DocumentFilters,
  EMPTY_FILTERS,
  effectiveSort,
  hasActiveFilterValues,
  hasActiveFilters,
  isRecursive,
  listingCapped,
  RECENT_DAYS,
  openedFolder,
} from "./state";
import type { RunWindow, SearchScopeRequest } from "./state";
import {
  connectionRootName,
  connectionRootOf,
  pinConnectionRoots,
  placeFor,
} from "./places";
import type { Places } from "./api";
import PlaceRows, { type PlaceRowTarget } from "./PlaceRows";
import {
  useDocumentTypeOptions,
  useDocumentsSearch,
  useFolderRecord,
  useFolderRows,
  useRecordsIndex,
} from "./useDocumentsSearch";
import { recordIconKind, recordMetaLabel } from "./records";
import { useDocumentColumns } from "@/components/FpPages/RMS/Data/documentColumns";
import { type ColumnKind } from "./state";
import EntityIcon from "@/components/FpPages/RMS/Dashboard/Components/EntityIcon";
import type { PortalService } from "@/components/FpFundBrain/Manage/Connections/InvestorPortalsRow";
import PortalsOverview from "../PortalsOverview";
import ServiceManagers from "../ServiceManagers";
import PortalRunsSection, { SyncPill } from "../PortalRunsSection";
import { ServiceMark } from "../ServiceMark";
import { Section } from "../HomeSections";
import { RecordTabRow } from "@/components/FpPages/RMS/Dashboard/RecordTabRow";
import {
  dayGroupOf,
  groupOf,
  groupRows,
  monthDayOf,
  serviceUploaderKey,
  type RowGroup,
} from "./portals";
import { useViewerTimeZone } from "@/hooks/useViewerTimeZone";

/** The Investor Portals overview's tabs (#15170): the portals it covers,
 *  the default, and their documents. */
type PortalsTab = "portals" | "documents";
const PORTALS_TABS: { key: PortalsTab; label: string }[] = [
  { key: "portals", label: "Portals" },
  { key: "documents", label: "Documents" },
];

/** Below this listing width the table drops the uploader label, the hover
 *  strip and half its Location column: the widest full row — name at its
 *  12rem floor, location, two dates, a type pill and three actions — needs
 *  about this much. */
export const TABLE_DENSE_BELOW_PX = 1000;

/** Below this the table cannot fit even dense — the destinations column and
 *  an open details pane leave a 1440 viewport about 500px for the card — so
 *  the listing folds to the compact row list the phone uses. */
export const TABLE_COMPACT_BELOW_PX = 720;

export interface DocumentsBrowserProps {
  /** Picker only (round 9, Claude Design): where the pinned group — Clear
   *  filters, then the count — renders, the modal's title row. The chips row
   *  then carries only controls, so at the modal's default width nothing
   *  scrolls and nothing clips. Absent, the group stays on the row's end. */
  pinnedHost?: HTMLElement | null;
  /** Picker only (round 10): list exactly these documents, wherever they
   *  are — the Selected scope (round 15). */
  onlyFileIds?: string[];
  /** Picker only (round 15, Claude Design): the picks as a scope of their
   *  own. From the first pick a `Selected N` chip stands at the end of the
   *  scope group and swaps the listing to the picks — `onlyFileIds` for the
   *  documents, `folders` for the folder picks — where the same checkboxes
   *  deselect in place. The picks used to be chips in the footer, which is
   *  the most constrained space in the modal (Hunter). */
  selectedScope?: {
    count: number;
    active: boolean;
    onChange: (active: boolean) => void;
    folders: FolderEntry[];
  };
  /** Put the cursor in the search field on mount — a modal that carries a
   *  search field focuses it on open (round 11, standing rule); the browser
   *  mounts with the modal, so a reopen focuses again. The host keeps it
   *  false where a soft keyboard would rise or where it focuses a field of
   *  its own. */
  autoFocusSearch?: boolean;
  state: BrowserState;
  onStateChange: (state: BrowserState) => void;
  mode: "page" | "picker";
  driveId?: string | null;
  /** Lowercased root folder names that are connections' places (round 17):
   *  hidden from a drive's root listing, since each is a place of its own.
   *  The page passes them; the picker passes `places` and they follow. */
  /** The places (round 17), for a picker: with drives on its root lists
   *  them as rows that drill in, and its trail starts at the place. */
  places?: Places;
  /** Where a search looks (round 18): everywhere unless the Location chip
   *  narrows it. The page derives the scope from the chip's value and names
   *  it, so a result count can restate a narrowed scope. */
  scope?: SearchScopeRequest;
  scopeLabel?: string;
  location?: LocationChipProps;
  excludeBlobKinds?: string[];
  rmsEnabled: boolean;
  /** Row ticking. The page passes its own for bulk delete; the picker passes
   *  the modal's shared selection. Absent, rows have no checkboxes. */
  selection?: DocumentsTableSelection;
  /** Opening a file. Defaults to the document viewer drawer. */
  onOpenFile?: (row: DocumentRow) => void;
  /** Bumped by the owner after a mutation so the listing refetches. */
  onRowsChanged?: () => void;
  /** Whether the listing is still waiting on its own reads (#12073): true
   *  until the first rows (or Home's sections) can paint. The page holds
   *  its organization-wide counts until this goes false, so they follow
   *  the listing instead of racing it. */
  onBusyChange?: (busy: boolean) => void;
  /** Picker only: the chips a selection surface earns (Claude Design, round
   *  5) — All documents / Recent / My uploads, Type and Related to; the rest
   *  live on the page. The host draws the folder trail itself, under its
   *  title. */
  chipSet?: "full" | "picker";
  /** Picker only, phone: the full-screen sheet — search first and full
   *  width, the chips as one scrolling row (Recent, My uploads, Related
   *  to), the compact rows. */
  phoneSheet?: boolean;
  /** Page only: the title row above the chips. */
  header?: React.ReactNode;
  /** Page only: where the page's search field renders — a slot in the top
   *  bar (round 15, Claude Design: one search per page, in chrome). The
   *  browser keeps owning the field (its debounce, its focus, its reset on
   *  Clear filters) and renders it into the slot, as it renders the pinned
   *  group into the picker's title row. */
  searchHost?: HTMLElement | null;
  /** Page only: the row menu's Details item. */
  onDetails?: (row: DocumentRow) => void;
  /** Page only: the row menu's Move item, where the reader can edit. */
  onMove?: (row: DocumentRow) => void;
  /** Page only: the row the details pane shows, and how a click sets it. */
  activeFileId?: string | null;
  /** Page only: Rename / Download / Delete from a folder row's menu. */
  onFolderAction?: (action: FolderAction, path: string[]) => void;
  /** Page only: the uploader keys that are the viewer, for Home's reasons. */
  viewerKeys?: string[];
  /** Page only, phone: rows as the compact list instead of the table. */
  compact?: boolean;
  /** Page only, record view: the vehicles a manager's listing rolls up when the
   *  viewer asked for them, and what the chips row shows after the chips
   *  (the Include vehicle documents toggle). */
  /** True while a record location's record is still resolving: the listing
   *  waits rather than fetching a slice the record may contradict. */
  awaitingRecord?: boolean;
  /** True while the places read is in flight with drives on (round 17):
   *  the listing's drive is not known until it lands, so nothing is
   *  fetched for a drive the page is about to leave. */
  awaitingPlaces?: boolean;
  /** Page only, record view: which vehicle a row came from, for its column. */
  /** Whether records are a destination in this organization (#6559 round
   *  6b). Below the coverage floor the Related to chip goes with the
   *  destinations: under the gate it is the weakest form of the concept
   *  standing in for the ones deliberately removed, and over an organization
   *  with no links it mostly returns nothing. The picker, which has no
   *  destinations column, is unaffected. */
  recordsEnabled?: boolean;
  /** Page only, Home: the records this viewer works with, and the way in. */
  recentRecords?: RecordSummary[];
  /** True while the recent records are still being read (#12073): Home
   *  holds every section until they are in. */
  awaitingRecentRecords?: boolean;
  /** True when a Recent records section is coming, so Home holds its place
   *  while busy rather than letting it pop in or out of the skeleton. */
  recentRecordsExpected?: boolean;
  /** Page only, record view: the record whose listing this is, so each row can
   *  name the OTHER records it touches and the listing can offer a way out to
   *  the ordinary table for what was never linked (#6559 round 6). */
  recordId?: string | null;
  /** Page only: opening a record as a location — from a Related to tag in
   *  the pane or the chip's Go to record. */
  onOpenRecord?: (record: LinkedEntity) => void;
  /** Page only: the organization's Investor Portals services (#11635 Round
   *  4). The portals view draws its overview from them and offers them as
   *  its Source picks; the phone's root listing gets a row to the view. */
  portalServices?: PortalService[];
  /** The services read is still out: the overview holds its place rather
   *  than saying "No services" about services that are about to arrive. */
  portalServicesLoading?: boolean;
  /** The investor portal service the page is inside, resolved by the page
   *  against `portalServices` (Round 7, R22); null on the overview, for a
   *  `svc` naming no service, and while the services load. Its name is
   *  `state.service`: the page clears that for a `svc` naming none. */
  portalService?: PortalService | null;
  /** A `svc` is open and the services that resolve it are still loading:
   *  the page is inside a service it cannot draw yet, so the listing waits
   *  rather than ask for a service that may not exist. */
  servicePending?: boolean;
  /** The services read failed: inside a service the page keeps the name
   *  the URL gave it, and the header says it could not be read. */
  portalServicesError?: boolean;
  /** Page only, phone: opening the portals view from its root-listing row. */
  onOpenPortals?: () => void;
  /** Page only: opening an investor portal service, a folder of its own
   *  (#11635 slice 1, Round 7, R21), from its tile. */
  onOpenService?: (service: string) => void;
  /** A portal page's sync history, its pill and its narrowing are shown
   *  (#15170): Finpilot staff only, as the page decides. */
  syncHistory?: boolean;
  /** Which sources the Source chip may offer and what it says while it has
   *  none to make (#15170): never a portal
   *  hidden from the viewer. Absent, every source the facet knows. */
  sourceGate?: SourceGate;
}

// A picker lists what can be attached, so Locked is not among its views.
const VIEW_OPTIONS: Array<{ key: BrowserView; label: string }> = [
  { key: "all", label: "All documents" },
  { key: "recent", label: "Recent" },
  { key: "mine", label: "My uploads" },
];

/** Below this frame width the desktop picker folds its view toggles into one
 *  chip (Claude Design, round 9). */
const PICKER_FOLD_BELOW_PX = 780;

/** Select all pages in at most this many documents (Claude Design, round
 *  10); past it the line states the limit. */
const SELECT_ALL_CEILING = 1000;

/** The chips row's one "on" treatment (CD sign-off note 2): the ink border
 *  the active filter chips take, without the clear segment, since one view
 *  is always on. Shared by the view tabs and the folded view chip. */
const CHIP_SHELL =
  "inline-flex h-[30px] items-center gap-1.5 rounded-[7px] border bg-[var(--fp-surface)] px-3 text-[12.5px] font-medium shadow-[var(--fp-lift)]";
/** The phone's three remaining views as chips under the search (round 17,
 *  ruling E). */
const VIEW_CHIPS: ReadonlyArray<{ view: BrowserView; label: string }> = [
  { view: "mine", label: "My uploads" },
  { view: "locked", label: "Locked" },
  { view: "processing", label: "Processing" },
];

const ACTIVE_CHIP_CLASS = `${CHIP_SHELL} border-[var(--fp-ink)] text-[var(--fp-ink)]`;
const INACTIVE_CHIP_CLASS = `${CHIP_SHELL} border-[var(--fp-border-strong)] text-[var(--fp-ink-2)] hover:border-[var(--fp-ink)] hover:text-[var(--fp-ink)]`;

/** The narrow picker's one view chip: labelled with the current view, it
 *  opens the three (Claude Design, round 9: "Recent", not "View: Recent"). */
const ViewMenuChip: React.FC<{
  options: ReadonlyArray<{ key: BrowserView; label: string }>;
  view: BrowserView;
  onChange: (view: BrowserView) => void;
}> = ({ options, view, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = options.find((option) => option.key === view)?.label ?? "";
  const close = () => setAnchor(null);
  return (
    <>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        // The name says what the control is, not only where it stands: the
        // unfolded form's tablist is "Documents view" too.
        aria-label={`Documents view: ${current}`}
        onClick={(e) => setAnchor(e.currentTarget)}
        className={ACTIVE_CHIP_CLASS}
      >
        {current}
        <CaretDownIcon size={12} aria-hidden />
      </button>
      {/* The chips row's own popover and items, not a second design system
          thirty pixels from the first. */}
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={chipPopoverSlotProps}
      >
        <div role="menu" aria-label="Documents view" className="w-[200px] py-1">
          {options.map((option) => {
            const checked = option.key === view;
            return (
              <button
                key={option.key}
                type="button"
                role="menuitemradio"
                aria-checked={checked}
                className={menuItemClass}
                onClick={() => {
                  close();
                  onChange(option.key);
                }}
              >
                <span className="w-4 text-[var(--fp-accent)]" aria-hidden>
                  {checked && <CheckIcon size={12} weight="bold" />}
                </span>
                {option.label}
              </button>
            );
          })}
        </div>
      </Popover>
    </>
  );
};

/** The chips row: wrapping on the page; one quiet scrolling strip on the
 *  phone sheet; and in a desktop picker one line whatever the modal's width
 *  — the controls scroll, and what is pinned at the right (`trailing`) never
 *  wraps under them (Claude Design, round 8). */
const ChipsRow: React.FC<{
  phoneSheet: boolean;
  /** One line with a pinned right end, the picker's shape. */
  strip?: boolean;
  trailing?: React.ReactNode;
  /** Inside a section, under its heading (a service page's Documents,
   *  Round 20, R69): the section's own gap sets it off, so no rule. */
  inSection?: boolean;
  children: React.ReactNode;
}> = ({ phoneSheet, strip = false, trailing, inSection = false, children }) =>
  phoneSheet ? (
    <QuietScrollStrip className="border-b border-[var(--fp-border)] px-4 pb-3 pt-2">
      <div className="flex w-max items-center gap-x-2 whitespace-nowrap">
        {children}
      </div>
    </QuietScrollStrip>
  ) : strip ? (
    <div
      data-testid="chips-strip"
      className="flex min-w-0 items-center gap-x-3 border-b border-[var(--fp-border)] pr-4"
    >
      <QuietScrollStrip className="min-w-0 flex-1 py-3 pl-4">
        {/* The strip's own width, not max-content: the controls share its
            slack (the search field grows to 240px, then gives it back as a
            chip takes a value), and only what they cannot give back overflows
            into the scroll. */}
        <div className="flex min-w-full items-center gap-x-3 whitespace-nowrap">
          {children}
        </div>
      </QuietScrollStrip>
      {trailing}
    </div>
  ) : (
    <div
      data-testid="chips-row"
      className={`flex flex-wrap items-center gap-x-4 gap-y-2 px-4 ${
        inSection ? "" : "border-b border-[var(--fp-border)] pb-3"
      }`}
    >
      {children}
      {trailing}
    </div>
  );

/** A manager group's facts on its header (Round 5, R3): the services that
 *  delivered its documents, each as its favicon, and the newest upload's
 *  day, from the rows loaded for the group. */
const GroupFacts: React.FC<{
  group: RowGroup;
  rows: DocumentRow[];
  services: PortalService[];
}> = ({ group, rows, services }) => {
  const zone = useViewerTimeZone();
  if (group.kind === "processing") return null;
  const hostOf = new Map(services.map((s) => [s.service, s.icon_host]));
  const delivered: string[] = [];
  for (const row of rows) {
    const label = row.uploadedBy?.label;
    if (label && !delivered.includes(label)) delivered.push(label);
  }
  // The newest upload whatever the table's sort: the stream arrives
  // newest-first but a column header can re-sort it.
  let newest: string | null = null;
  for (const row of rows)
    if (row.uploadedAt && (newest === null || row.uploadedAt > newest))
      newest = row.uploadedAt;
  return (
    <span className="flex min-w-0 items-center gap-2 text-[12.5px] text-[var(--fp-ink-3)]">
      {delivered.length > 0 && (
        <span className="flex items-center gap-1">
          {delivered.map((service) => (
            <ServiceMark
              key={service}
              service={service}
              host={hostOf.get(service) ?? null}
              size={14}
            />
          ))}
        </span>
      )}
      {newest && (
        <span className="truncate">
          · newest{" "}
          {monthDayOf({ granularity: "instant", at: newest, zone }, zone)}
        </span>
      )}
    </span>
  );
};

/** The hairline between a service page's blocks (#11635 Round 16, R61):
 *  inset like the headings, in the middle of a 40px band, so the blocks
 *  read as one clean column. Only a service's page draws it. */
const SectionRule: React.FC = () => (
  <hr
    data-testid="section-rule"
    className="mx-4 my-5 border-0 border-t border-solid border-[var(--fp-border)]"
  />
);

const DocumentsBrowser: React.FC<DocumentsBrowserProps> = ({
  state,
  onStateChange,
  mode,
  driveId,
  places,
  scope,
  scopeLabel,
  location,
  excludeBlobKinds,
  rmsEnabled,
  selection,
  onOpenFile,
  onRowsChanged,
  onBusyChange,
  chipSet = "full",
  phoneSheet = false,
  header,
  searchHost = null,
  onDetails,
  onMove,
  activeFileId = null,
  onFolderAction,
  viewerKeys = [],
  compact: compactProp = false,
  awaitingRecord = false,
  awaitingPlaces = false,
  recordsEnabled = true,
  recentRecords,
  awaitingRecentRecords = false,
  recentRecordsExpected = false,
  recordId = null,
  onOpenRecord,
  pinnedHost = null,
  onlyFileIds,
  selectedScope,
  autoFocusSearch = false,
  portalServices = [],
  portalServicesLoading = false,
  portalService = null,
  servicePending = false,
  portalServicesError = false,
  onOpenPortals,
  onOpenService,
  syncHistory = false,
  sourceGate,
}) => {
  // The Investor Portals view (#11635 Round 4): the services overview above
  // the portal documents grouped by manager. Page only; a picker has no
  // such view.
  const portals = mode === "page" && state.view === "portals";
  const portalSourceKeys = useMemo(
    () => portalServices.map(serviceUploaderKey),
    [portalServices]
  );
  // The page's state names a service only when it is inside one: resolved
  // or still resolving (the page clears a `svc` naming none it lists).
  const inService = portals && state.service !== null;
  // The overview's two tabs (#15170): the portals it covers, the default,
  // and their documents.
  const [portalsTab, setPortalsTab] = useState<PortalsTab>("portals");
  // The manager groups fold, Processing open from the start (R3).
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(["processing"])
  );
  const toggleGroup = useCallback(
    (key: string) =>
      setOpenGroups((opened) => {
        const next = new Set(opened);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    []
  );
  // A picker lists nothing that cannot be picked: failed documents stay out
  // of it (Claude Design, round 8). The page lists them like everything else.
  // The listing's drive: the page's, or the one the picker's state carries
  // once a place is opened (round 17).
  const listingDriveId = driveId ?? state.drive ?? null;
  // The picker's root with drives on lists the places themselves (round 17,
  // Claude Design ruling D): nothing is in a drive yet, so there is no
  // listing to fetch until a place is opened. A term, a chip or a named
  // scope answers across everything instead.
  // The phone's Documents tab root is the same rows (ruling E): the page
  // there has no column, so the places are the listing.
  const atPlacesRoot =
    (mode === "picker" || compactProp) &&
    places?.drivesEnabled === true &&
    state.view === "all" &&
    listingDriveId === null &&
    state.path.length === 0 &&
    !hasActiveFilters(state) &&
    onlyFileIds === undefined;
  // Drives off on the phone: the connected apps have no column to sit in,
  // so their rows lead the tree's root listing.
  const appsAboveListing =
    mode === "page" &&
    compactProp &&
    places !== undefined &&
    !places.drivesEnabled &&
    places.connectedApps.length > 0 &&
    state.view === "all" &&
    state.path.length === 0 &&
    !hasActiveFilters(state);
  const portalsAboveListing =
    mode === "page" &&
    compactProp &&
    portalServices.length > 0 &&
    state.view === "all" &&
    listingDriveId === null &&
    state.path.length === 0 &&
    !hasActiveFilters(state);
  const currentPlace = useMemo(
    () => placeFor(places, listingDriveId, state.path),
    [places, listingDriveId, state.path]
  );
  // The Shared drives row opens its listing (round 18); a drive or a
  // connection opens on its root.
  const openPlace = (target: PlaceRowTarget) =>
    onStateChange(
      target.view
        ? { ...openedFolder(state, []), view: target.view, drive: null }
        : { ...openedFolder(state, target.path), drive: target.driveId }
    );
  // The Shared drives listing: the drives as rows, until a term or a chip
  // turns the view into results.
  const placesGroup: "drives" | null =
    places !== undefined && state.view === "drives" && !hasActiveFilters(state)
      ? "drives"
      : null;
  const searchOptions = useMemo(
    () => ({
      driveId: listingDriveId,
      scope,
      excludeBlobKinds,
      blobIds: onlyFileIds,
      // The overview lists the documents of the portals it lists (#15170).
      portalKeys: portals ? portalSourceKeys : undefined,
    }),
    [
      listingDriveId,
      scope,
      excludeBlobKinds,
      onlyFileIds,
      portals,
      portalSourceKeys,
    ]
  );
  // Home is a landing view: the chips filter its sections, but a search term
  // wants the whole result list, so typing takes the user to results. So
  // does an attribute clause: Home's sections are filtered in the browser
  // from rows that carry no custom values, so only the listing can answer.
  const home =
    mode === "page" &&
    state.view === "home" &&
    state.q.trim() === "" &&
    state.filters.columns.length === 0 &&
    state.filters.location === null;
  // The organization's custom document columns (round 8b): the Attributes
  // chip's menu. The picker keeps to the kinds that survive its one row.
  const documentColumns = useDocumentColumns();
  const columnKinds: ColumnKind[] | undefined =
    mode === "picker" ? ["enum", "boolean"] : undefined;

  // Home reads its own queries; the listing search stands down there. So does
  // a record location whose record has not resolved yet: which slice of the
  // record leads depends on its kind and its folder, so a search issued before
  // the record arrives fetches the wrong one and shows it (#6559 round 7).
  // The places root lists places, not folders, so neither query runs there.
  // Inside a service the services have not resolved yet, the listing waits:
  // the `svc` may name no service, and its overview lists every portal
  // upload (R22).
  const awaitingService = servicePending;
  // The overview's documents are read on its Documents tab only, once the
  // listed portals are known, and never with none to list: an empty key
  // list would narrow nothing (#15170).
  const overviewIdle =
    portals &&
    !inService &&
    (portalsTab === "portals" ||
      portalServicesLoading ||
      portalSourceKeys.length === 0);
  const search = useDocumentsSearch(state, {
    ...searchOptions,
    enabled:
      !home &&
      !awaitingRecord &&
      !awaitingPlaces &&
      !awaitingService &&
      !overviewIdle &&
      !atPlacesRoot &&
      placesGroup === null,
  });
  const folders = useFolderRows(state, {
    ...searchOptions,
    enabled:
      !awaitingPlaces &&
      !awaitingService &&
      !atPlacesRoot &&
      placesGroup === null,
  });
  // A connection's root folder lists first in its drive, with the app's
  // mark (round 18g, Hunter); opening it is opening the place. A search's
  // folder hits keep their order by name (#12088): they are answers, not a
  // listing of the drive, so nothing is pinned above the rest.
  const folderNodes = useMemo(
    () =>
      isRecursive(state)
        ? (folders.data ?? [])
        : pinConnectionRoots(folders.data ?? [], places, listingDriveId),
    [folders.data, places, listingDriveId, state]
  );
  // A folder row is looked up in its own drive (#12088): a search's hits
  // come from every drive the search spanned, and a browse row's drive is
  // the listing's; a row that names no drive (an organization without
  // drives) is in the listing's.
  const folderApp = useCallback(
    (folder: { filePath: string[]; driveId?: string | null }): string | null =>
      connectionRootOf(
        places,
        folder.driveId ?? listingDriveId,
        folder.filePath
      )?.app.app ?? null,
    [places, listingDriveId]
  );
  // A connection's root is named the way the column names the place, not
  // by its stored folder (#12074): "SharePoint", not "Sharepoint".
  const folderName = useCallback(
    (folder: { filePath: string[]; driveId?: string | null }): string | null =>
      connectionRootName(
        places,
        folder.driveId ?? listingDriveId,
        folder.filePath
      ),
    [places, listingDriveId]
  );
  const typeOptions = useDocumentTypeOptions();

  // A fixed set of ids is listed wherever the documents are: recursive.
  const recursive = isRecursive(state) || onlyFileIds !== undefined;
  // The table keys its density on its own frame (house frame rule): beside
  // the destinations column and a details pane the card can drop under
  // 1000px, where the full column set no longer fits.
  const { frameRef, frameWidth } = useFrameWidth<HTMLDivElement>();
  // The picker's modal is narrow by design and its rows carry the shared
  // selection's checkboxes, which the compact list has no room for: the
  // folds are the page's.
  const dense =
    mode === "page" && frameWidth !== null && frameWidth < TABLE_DENSE_BELOW_PX;
  const compact =
    compactProp ||
    phoneSheet ||
    (mode === "page" &&
      frameWidth !== null &&
      frameWidth < TABLE_COMPACT_BELOW_PX);
  // Below 780px the desktop picker's three view toggles fold into one chip
  // that reads the current view (Claude Design, round 9); above it one-click
  // switching is worth more than the chip's tidiness. Keyed on the frame,
  // never the viewport (house rule).
  const narrowPicker =
    mode === "picker" &&
    !phoneSheet &&
    frameWidth !== null &&
    frameWidth < PICKER_FOLD_BELOW_PX;
  // A debounced write fires after the render that scheduled it, so it must
  // write over the state of that moment, not the one it captured — a chip
  // picked during the debounce would otherwise be undone.
  const stateRef = useRef(state);
  stateRef.current = state;
  // The Documents section of the portals view, which a sync's count scrolls
  // to (Round 15, R50).
  const documentsSectionRef = useRef<HTMLElement>(null);
  const update = (patch: Partial<BrowserState>) =>
    onStateChange({ ...stateRef.current, ...patch });

  const setView = (view: BrowserView) => {
    // A scope pick leaves the Selected scope: the picks are a way of looking,
    // and the view is where the reader goes back to.
    selectedScope?.onChange(false);
    update({ view, sort: null, run: null });
  };
  const setFilters = (filters: DocumentFilters) => update({ filters });
  // Clear filters clears the search term with the chips (Hunter, round 15):
  // a term is a filter the reader typed rather than picked, and a control
  // that left "memo" in the field cleared half of what narrowed the listing.
  // The field debounces its draft, so a Clear that lands inside that window
  // would be overwritten by the keystrokes it was meant to clear; remounting
  // the field drops the pending draft with the term.
  const [searchGeneration, setSearchGeneration] = useState(0);
  const clearFilters = () => {
    setSearchGeneration((n) => n + 1);
    update({ filters: EMPTY_FILTERS, q: "" });
  };
  const setSort = (sort: BrowserSort) => update({ sort });
  // A folder row names its drive (#12088): a search's folder hits come from
  // every drive the search spanned, so opening one is arriving in its drive.
  // A row in the listing's own drive names the drive the reader is in, and
  // the state is left as it was.
  const openFolder = (
    path: string[],
    focus?: string,
    driveId?: string | null
  ) => {
    selectedScope?.onChange(false);
    const next = openedFolder(stateRef.current, path, focus ?? null);
    onStateChange(
      driveId === undefined || driveId === listingDriveId
        ? next
        : { ...next, drive: driveId }
    );
  };
  const open =
    onOpenFile ??
    ((row: DocumentRow) =>
      openFile({
        fileId: row.fileId,
        fileName: row.fileName,
        fileType: row.fileType,
        unreadableReason: row.unreadableReason,
      }));

  // A zero badge advertises an empty destination (CD ruling C): the count
  // shows only once there is something locked.
  // The phone sheet keeps only the two named views; a lit one taps off.
  const viewOptions = VIEW_OPTIONS.filter(
    (option) => !(phoneSheet && option.key === "all")
  );

  const typeLabels = useMemo(
    () =>
      Object.fromEntries((typeOptions.data ?? []).map((t) => [t.key, t.label])),
    [typeOptions.data]
  );

  // The count describes the listing (CD ruling G): folders and documents in
  // a browse listing, documents alone in a recursive one; never zero while
  // rows are on screen. Home has sections, not a count.
  const total = search.total;
  const folderCount = recursive ? 0 : folderNodes.length;
  // A browse listing is two requests, and it paints once both are in: the
  // folders lead the files, and a listing that reshapes under the reader is
  // worse than a longer skeleton (Claude Design, round 9). The folder hook
  // says itself whether it still blocks.
  const listingLoading =
    search.isLoading ||
    awaitingRecord ||
    awaitingPlaces ||
    awaitingService ||
    (portals && !inService && portalServicesLoading) ||
    folders.blocking;
  // Home reads its own queries, so it says itself when it is busy; the
  // listing's busy state is the table's. Reported upward as one signal.
  const [homeBusy, setHomeBusy] = useState(true);
  const busy = home ? homeBusy : listingLoading;
  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);
  // While a query is live the count says "results": that is the one string on
  // the page that needs to know a search is happening, which is why the
  // heading no longer does (Claude Design, round 7d).
  // Not under Show selected, where the term narrows nothing and the count
  // is the picks', not results.
  const searching = state.q.trim() !== "" && onlyFileIds === undefined;
  const countLabel = (() => {
    if (home) return null;
    // The places root counts places, not documents (round 17).
    if (atPlacesRoot && places) {
      const driveCount = (places.myDrive ? 1 : 0) + places.sharedDrives.length;
      // Apps, not connections: two SharePoint sites are one app.
      const appCount = places.connectedApps.length;
      return `${driveCount} drive${driveCount === 1 ? "" : "s"} · ${appCount} app${
        appCount === 1 ? "" : "s"
      }`;
    }
    if (placesGroup && places) {
      const n = places.sharedDrives.length;
      return `${n} drive${n === 1 ? "" : "s"}`;
    }
    // Always rendered: while the listing is in flight the count says so,
    // and the real number replaces it in place (Claude Design, round 8). A
    // listing that failed has no count; the table's error row says why.
    if (search.isError) return null;
    if (total === null || listingLoading) return "Searching…";
    const parts: string[] = [];
    if (folderCount > 0)
      parts.push(`${folderCount} folder${folderCount === 1 ? "" : "s"}`);
    if (total > 0 || parts.length === 0)
      parts.push(
        searching
          ? // Results restate the scope (round 17, Claude Design), so no one
            // reads a partial answer as the whole one.
            `${total.toLocaleString()} result${total === 1 ? "" : "s"}${
              scopeLabel ? ` in ${scopeLabel}` : ""
            }`
          : `${total.toLocaleString()} document${total === 1 ? "" : "s"}`
      );
    return parts.join(" · ");
  })();
  const anyFilter = hasActiveFilterValues(state.filters);

  const scopeActive = selectedScope?.active === true;
  // The Selected scope chip (round 15): last in the group, from the first
  // pick, active while the picks are the listing.
  const selectedChip =
    selectedScope && selectedScope.count > 0 ? (
      <button
        type="button"
        role="tab"
        aria-selected={scopeActive}
        onClick={() => selectedScope.onChange(!scopeActive)}
        className={scopeActive ? ACTIVE_CHIP_CLASS : INACTIVE_CHIP_CLASS}
      >
        Selected {selectedScope.count.toLocaleString()}
      </button>
    ) : null;
  // A places listing (Shared drives) is none of the three views: the
  // toggles stand down there rather than reading blank.
  const viewToggles = placesGroup ? null : narrowPicker ? (
    <div className="flex gap-1">
      <ViewMenuChip
        options={viewOptions}
        view={state.view}
        onChange={setView}
      />
      {selectedChip && (
        <div role="tablist" aria-label="Selected">
          {selectedChip}
        </div>
      )}
    </div>
  ) : (
    <div role="tablist" aria-label="Documents view" className="flex gap-1">
      {viewOptions.map((option) => {
        const selected = !scopeActive && option.key === state.view;
        return (
          <button
            key={option.key}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={
              selected
                ? phoneSheet
                  ? () => setView("all")
                  : undefined
                : () => setView(option.key)
            }
            className={selected ? ACTIVE_CHIP_CLASS : INACTIVE_CHIP_CLASS}
          >
            {option.label}
          </button>
        );
      })}
      {selectedChip}
    </div>
  );

  // Infinite scroll: observe a sentinel under the last row rather than asking
  // the reader to press a button for every hundred documents. `rootMargin`
  // starts the fetch a screen early so the list rarely stalls, and the guard
  // on `isFetchingNextPage` keeps a fast scroll from queueing several pages.
  //
  // The root MUST be the scrolling container. The listing scrolls inside the
  // frame below, not the viewport, so against the default root the sentinel is
  // clipped by that container and `rootMargin` — which only grows the root's
  // own rect — buys nothing: the fetch starts when the sentinel is genuinely
  // on screen and the reader watches a Loading line. Measured, not reasoned.
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const { isFetchingNextPage, fetchNextPage } = search;
  // Recent stops at its cap: past it the sentinel is gone and nothing pages.
  const hasNextPage =
    search.hasNextPage &&
    !(onlyFileIds === undefined && listingCapped(state, search.rows.length));
  useEffect(() => {
    const node = loadMoreRef.current;
    if (node === null || !hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void fetchNextPage();
      },
      { root: frameRef.current, rootMargin: "600px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
    // `frameRef` is a stable ref object; its current value is read at
    // subscribe time, which is after the frame has mounted.
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, frameRef]);

  // The one transition worth animating: Home's sections giving way to a
  // results table, and back. It fires on the crossing itself, never on a
  // keystroke — a page that fades on every character typed is a page that
  // flickers while someone spells "acasta" — so the key changes only when the
  // listing swaps kind (Claude Design, round 7d).
  const bodyKind = home ? "home" : "listing";
  const previousKind = useRef(bodyKind);
  const [crossed, setCrossed] = useState(false);
  useEffect(() => {
    if (previousKind.current === bodyKind) return;
    previousKind.current = bodyKind;
    setCrossed(true);
  }, [bodyKind]);

  const rowsChanged = () => {
    void search.refetch();
    onRowsChanged?.();
  };

  // The records column: "Also linked to" inside a record location, where the
  // record itself is redundant, and "Related to" in every flat or recursive
  // listing and in search results. Never inside a folder — 83% to 99% of the
  // documents under `Managers/Acme` belong to Acme, so a column repeating it
  // costs width to say nothing (#6559 round 7). The picker keeps its narrow
  // set of columns, and a dense card drops it with the other columns that do
  // not fit.
  const recordColumn = dense
    ? undefined
    : recordId && mode === "page"
      ? {
          label: "Also linked to",
          of: (row: DocumentRow) => ({
            records: row.linkedEntities.filter(
              (entity) => entity.publicId !== recordId
            ),
            // Every other record, not only the ones the row carries.
            total: Math.max(row.linkedEntityCount - 1, 0),
          }),
        }
      : recordsEnabled && recursive && mode === "page"
        ? {
            label: "Related to",
            of: (row: DocumentRow) => ({
              records: row.linkedEntities,
              total: row.linkedEntityCount,
            }),
          }
        : undefined;

  // What an empty listing says, and the control that fixes it (Claude
  // Design, round 8): a search with no hits offers to clear the search; a
  // filter with none offers to clear the filters; an empty Recent names its
  // window and opens the door to everything by putting the cursor in the
  // search field — which is a search over All documents, so the view goes
  // with it.
  const searchFieldRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (autoFocusSearch) searchFieldRef.current?.focus();
    // The page's field arrives with its slot (the phone's search mode
    // mounts the slot on open), so the slot is a dependency too.
  }, [autoFocusSearch, searchHost]);
  const emptyActionClass =
    "mt-2 text-[12.5px] font-medium text-[var(--fp-ink-2)] hover:text-[var(--fp-ink)]";
  const emptyState: React.ReactNode =
    mode === "picker" ? (
      searching ? (
        <span className="flex flex-col items-center">
          <span>No documents match &ldquo;{state.q.trim()}&rdquo;</span>
          {anyFilter ? (
            <button
              type="button"
              onClick={clearFilters}
              className={emptyActionClass}
            >
              Clear filters
            </button>
          ) : (
            <button
              type="button"
              onClick={() => update({ q: "" })}
              className={emptyActionClass}
            >
              Clear search
            </button>
          )}
        </span>
      ) : anyFilter ? (
        <span className="flex flex-col items-center">
          <span>No documents match these filters</span>
          <button
            type="button"
            onClick={clearFilters}
            className={emptyActionClass}
          >
            Clear filters
          </button>
        </span>
      ) : state.view === "recent" ? (
        <span className="flex flex-col items-center">
          <span>No documents uploaded in the last {RECENT_DAYS} days</span>
          <button
            type="button"
            onClick={() => {
              setView("all");
              searchFieldRef.current?.focus();
            }}
            className={emptyActionClass}
          >
            Search all documents
          </button>
        </span>
      ) : undefined
    ) : state.view === "recent" && !searching && !anyFilter ? (
      // The page shares Recent's window, so its empty state names it too;
      // the page's search field is its own and needs no door.
      <span>No documents uploaded in the last {RECENT_DAYS} days</span>
    ) : portals && portalServicesError ? (
      // The portals could not be read (#15170): the page says so above, and
      // nothing here claims the portals delivered nothing.
      <span>No documents to show</span>
    ) : portals && !searching && !anyFilter ? (
      <span>No documents from investor portals yet</span>
    ) : undefined;

  // Select all across pages (round 10, Claude Design): once every loaded
  // selectable file is ticked, one line offers the rest — pages them in,
  // then ticks them — under a ceiling; past it the line states the limit
  // and offers nothing. One async handler, not an effect: a page that fails
  // ends the run where it stands, and the line goes back to its offer.
  const setAll = selection?.setAll;
  const pickableLoaded = useMemo(
    () =>
      selection
        ? search.rows.filter((row) => rowSelectable(row, selection))
        : [],
    [search.rows, selection]
  );
  const pickedLoaded = selection
    ? pickableLoaded.filter((row) => selection.isSelected(row.fileId)).length
    : 0;
  const loadedAllPicked =
    pickableLoaded.length > 0 && pickedLoaded === pickableLoaded.length;
  const [selectingAll, setSelectingAll] = useState(false);
  // One run at a time: a second header click while pages are still coming
  // in would start a second run whose end clears "Selecting…" under the
  // first. A ref, since the state may be a render behind the click.
  const selectingRef = useRef(false);
  const selectAllMatching = async () => {
    if (setAll === undefined || selection === undefined) return;
    if (!hasNextPage) {
      setAll(pickableLoaded.slice(0, SELECT_ALL_CEILING), true);
      return;
    }
    if (selectingRef.current) return;
    selectingRef.current = true;
    setSelectingAll(true);
    try {
      let result = await search.fetchNextPage();
      const loaded = () =>
        result.data?.pages.flatMap((page) => page.files) ?? [];
      // A failed page resolves rather than throws (the query keeps its
      // earlier pages and reports the error); it ends the run unticked, and
      // the line goes back to its offer.
      while (
        !result.isError &&
        result.hasNextPage &&
        loaded().length < SELECT_ALL_CEILING &&
        (onlyFileIds !== undefined || !listingCapped(state, loaded().length))
      ) {
        result = await result.fetchNextPage();
      }
      // A page that failed mid-run: the pages that did load are ticked, so
      // the line stands with its offer for the rest rather than vanishing.
      // The ceiling binds the tick itself, not only the paging: the reader
      // may have scrolled more pages in since the last run.
      setAll(
        loaded()
          .filter((row) => rowSelectable(row, selection))
          .slice(0, SELECT_ALL_CEILING),
        true
      );
    } finally {
      selectingRef.current = false;
      setSelectingAll(false);
    }
  };
  // Select all selects all (Hunter, round 11, over the round-10 two-step):
  // the header checkbox pages every match in and ticks it; the line only
  // reports, and offers the rest when a run stopped short.
  const tableSelection =
    selection && setAll
      ? {
          ...selection,
          setAll: (rows: DocumentRow[], selected: boolean) => {
            if (selected) void selectAllMatching();
            else setAll(rows, selected);
          },
        }
      : selection;
  const multiPage = (search.data?.pages.length ?? 0) > 1;
  const selectAllLine = (() => {
    if (
      mode !== "picker" ||
      setAll === undefined ||
      total === null ||
      onlyFileIds !== undefined
    )
      return null;
    if (selectingAll) return { text: "Selecting…", action: null };
    if (!loadedAllPicked) return null;
    if (hasNextPage) {
      if (pickedLoaded >= SELECT_ALL_CEILING)
        return {
          text: `${pickedLoaded.toLocaleString()} of ${total.toLocaleString()} documents selected. Select up to ${SELECT_ALL_CEILING.toLocaleString()} at once.`,
          action: null,
        };
      // The offer promises what the run delivers: the rest, or the ceiling.
      return {
        text: `${pickedLoaded.toLocaleString()} of ${total.toLocaleString()} documents selected.`,
        action: {
          label:
            total > SELECT_ALL_CEILING
              ? `Select up to ${SELECT_ALL_CEILING.toLocaleString()}`
              : `Select all ${total.toLocaleString()}`,
          run: () => void selectAllMatching(),
        },
      };
    }
    if (!multiPage) return null;
    return {
      text: `All ${pickedLoaded.toLocaleString()} documents selected.`,
      action: {
        label: "Clear selection",
        run: () => setAll(pickableLoaded, false),
      },
    };
  })();

  // The portal documents by manager (spec R20): the rows reordered so each
  // group's rows sit together, in the order the groups' newest rows arrive.
  // A portal's page groups them by the day they arrived instead (#15170):
  // Today, Yesterday, Earlier, in the viewer's zone.
  const viewerZone = useViewerTimeZone();
  const rowGroupOf = useMemo((): ((row: DocumentRow) => RowGroup) => {
    if (!inService) return groupOf;
    const now = new Date();
    return (row) => dayGroupOf(row, now, viewerZone);
  }, [inService, viewerZone]);
  const groupedRows = useMemo(
    () => (portals ? groupRows(search.rows, rowGroupOf) : search.rows),
    [portals, search.rows, rowGroupOf]
  );
  const body =
    atPlacesRoot && places ? (
      <PlaceRows
        places={places}
        group="root"
        onOpenPlace={openPlace}
        compact={compact}
      />
    ) : placesGroup && places ? (
      <PlaceRows
        places={places}
        group={placesGroup}
        onOpenPlace={openPlace}
        compact={compact}
      />
    ) : home ? (
      <DocumentsHome
        state={state}
        searchOptions={searchOptions}
        typeLabels={typeLabels}
        viewerKeys={viewerKeys}
        onOpenFolder={openFolder}
        onOpenFile={open}
        activeFileId={activeFileId}
        onDetails={onDetails}
        onMove={onMove}
        onRowsChanged={rowsChanged}
        onBusyChange={setHomeBusy}
        places={places}
        awaitingPlaces={awaitingPlaces}
        records={recordsEnabled ? recentRecords : []}
        awaitingRecentRecords={awaitingRecentRecords}
        recentRecordsExpected={recentRecordsExpected}
        onOpenRecord={mode === "page" ? onOpenRecord : undefined}
        compact={compact}
        dense={dense}
      />
    ) : (
      <DocumentsTable
        rows={groupedRows}
        groups={
          portals && inService
            ? { of: rowGroupOf }
            : portals
              ? {
                  of: groupOf,
                  onOpenRecord: mode === "page" ? onOpenRecord : undefined,
                  isOpen: (key) => openGroups.has(key),
                  onToggle: toggleGroup,
                  decorate: (group, rows) => (
                    <GroupFacts
                      group={group}
                      rows={rows}
                      services={portalServices}
                    />
                  ),
                }
              : undefined
        }
        // A browse listing's folders, or a search's folder hits (#12088);
        // a chip, a named view or a record location lists none.
        folders={
          onlyFileIds !== undefined
            ? (selectedScope?.folders ?? [])
            : folderNodes
        }
        showLocation={recursive}
        places={places}
        folderApp={folderApp}
        folderName={folderName}
        sort={effectiveSort(state)}
        onSortChange={setSort}
        onOpenFolder={openFolder}
        onOpenFile={open}
        selection={tableSelection}
        rowActions={mode === "page"}
        typeLabels={typeLabels}
        activeFileId={activeFileId}
        focusFileId={state.focus}
        onDetails={mode === "page" ? onDetails : undefined}
        onMove={mode === "page" ? onMove : undefined}
        onFolderAction={mode === "page" ? onFolderAction : undefined}
        recordColumn={recordColumn}
        onOpenRecord={onOpenRecord}
        compact={compact}
        dense={dense}
        picker={mode === "picker"}
        embedded={portals}
        flushHeader={portals}
        emptyState={emptyState}
        hideHeaderWhenEmpty={portals}
        isLoading={listingLoading}
        stale={search.isPlaceholderData}
        isError={search.isError}
        onRowsChanged={rowsChanged}
      />
    );
  // The portals view stacks its sections the way Home does (spec R18), one
  // scrolling card. The overview: Services, Documents. A
  // service's page reads top to bottom (Round 8, R26): the service, its
  // managers, its documents, its sync history (Round 13, R42). No mail
  // shows in either (R41).
  // The next page's sentinel sits at the foot of the documents, not of
  // the card: below a later section it would stay in view while every
  // group is folded, and page after page would load unasked.
  const loadMore = !home && hasNextPage && (
    <div
      ref={loadMoreRef}
      data-testid="documents-load-more"
      className="py-3 text-center text-[12.5px] text-[var(--fp-ink-3)]"
    >
      {search.isFetchingNextPage ? "Loading…" : "\u00a0"}
    </div>
  );

  // A chip that has stood down cannot be seen or cleared, so the filter it
  // owned goes with it: a bookmarked URL must not leave the listing silently
  // narrowed by a control that is no longer on the page (#6559 round 6b).
  useEffect(() => {
    if (recordsEnabled || state.filters.entities.length === 0) return;
    onStateChange({
      ...state,
      filters: { ...state.filters, entities: [] },
    });
  }, [recordsEnabled, state, onStateChange]);

  // Typing a record's name is the reflex, so meet it: matching records sit
  // above the file hits. This is the one records surface that ships to every
  // organization — it works at any level of linking (#6559 round 6).
  const term = state.q.trim();
  // The picker too (round 10, Claude Design): the same line items, and a
  // record chosen there becomes the picker's location.
  // Not under Show selected: the picks are the listing and the term does
  // not narrow them, so a record hit would be an offer over nothing.
  const wantsRecords =
    rmsEnabled &&
    onOpenRecord !== undefined &&
    term.length >= 2 &&
    onlyFileIds === undefined;
  const recordHits = useRecordsIndex({ q: term, limit: 3 }, wantsRecords);
  const searchSuggestions =
    wantsRecords && state.view !== "record"
      ? (recordHits.data?.records ?? []).slice(0, 3)
      : [];

  // The folder-to-record handoff (#6559 round 7). Every organization files its
  // documents under `Managers/<record name>/` and `Vehicles/<record name>/`,
  // so the folder the reader is standing in is where the record concept is
  // already on screen. Only in a folder browse, and only where exactly one
  // record wears the name. It renders as the same row a search hit does
  // (round 14): the sentence that used to state the record's counts against
  // the folder's read as a claim the folder was missing most of its
  // documents, and Hunter asked for the row instead. A folder browse and a
  // search are exclusive (a term makes the listing recursive, which idles
  // the folder query), so at most one source has rows and they concatenate.
  const folderRecord = useFolderRecord(
    mode === "page" &&
      recordsEnabled &&
      onOpenRecord !== undefined &&
      state.view === "all" &&
      !recursive &&
      state.path.length > 0
      ? state.path
      : null
  );
  const suggestions = [
    ...searchSuggestions,
    ...(folderRecord.data ? [folderRecord.data] : []),
  ];

  /* A pinned right-hand group (CD ruling B): the clear control, then the
     count, always in that order, so the chips never shift. In the desktop
     picker it lives in the modal's title row (round 9). */
  // A service page carries its chips inside its Documents section, whose
  // heading already counts the listing (Round 20, R69).
  // The overview's Documents tab does the same (#15170), and its Portals
  // tab carries none: the chips narrow documents, not the portals.
  const bandChips =
    portals &&
    (inService ? portalService !== null : portalsTab === "documents");
  const chipsAbove = !portals || (inService && portalService === null);
  const pinnedGroup = (
    <div className="ml-auto flex shrink-0 items-center gap-3">
      {anyFilter && !phoneSheet && (
        <button
          type="button"
          onClick={clearFilters}
          className="h-[30px] text-[12.5px] font-medium text-[var(--fp-ink-2)] hover:text-[var(--fp-ink)]"
        >
          Clear filters
        </button>
      )}
      {/* The phone sheet's strip scrolls; anything at its far end would
          only ever be reached by scrolling past the chips, and each chip
          clears itself. */}
      {countLabel && !phoneSheet && !bandChips && (
        <span
          data-testid="listing-count"
          className="whitespace-nowrap text-[12.5px] text-[var(--fp-ink-3)] [font-variant-numeric:tabular-nums]"
        >
          {countLabel}
        </span>
      )}
    </div>
  );
  const pinnedTarget = mode === "picker" && !phoneSheet ? pinnedHost : null;
  // The filter chips: above the listing, or on a service page under its
  // Documents heading (Round 20, R69).
  const filterChipsRow = onlyFileIds === undefined && (
    <ChipsRow
      phoneSheet={phoneSheet}
      strip={mode === "picker"}
      trailing={pinnedTarget ? null : pinnedGroup}
      inSection={bandChips}
    >
      {/* The picker's trail starts at the first folder: at the root it
        would only restate the active view directly above it (CD extra
        ruling). The page's title row carries the trail instead. */}
      {mode === "picker" &&
        chipSet === "full" &&
        state.view === "all" &&
        state.path.length > currentPlace.root.length && (
          <Breadcrumbs
            path={state.path}
            base={currentPlace.root}
            rootLabel={currentPlace.name}
            onNavigate={openFolder}
          />
        )}
      {/* The phone's three remaining views as chips under the search
          (round 17, ruling E): the tab strip has no room for them and
          Recent already has a tab. A pressed chip is the view; pressing
          it again returns to the listing. */}
      {mode === "page" &&
        compact &&
        (state.view === "all" ||
          VIEW_CHIPS.some((chip) => chip.view === state.view)) && (
          <div
            role="group"
            aria-label="Views"
            className="flex shrink-0 items-center gap-1.5"
          >
            {VIEW_CHIPS.map(({ view, label }) => {
              const on = state.view === view;
              return (
                <button
                  key={view}
                  type="button"
                  aria-pressed={on}
                  onClick={() =>
                    onStateChange({
                      ...state,
                      view: on ? "all" : view,
                      sort: null,
                      q: "",
                      run: null,
                    })
                  }
                  className={on ? ACTIVE_CHIP_CLASS : INACTIVE_CHIP_CLASS}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      <FilterChips
        // One scrolling row on a phone: six chips wrapping onto two lines
        // spent a third of the first screen before a document appeared.
        scrolling={compact}
        strip={mode === "picker" && !phoneSheet}
        filters={state.filters}
        onChange={setFilters}
        typeOptions={typeOptions.data ?? []}
        rmsEnabled={rmsEnabled}
        location={location}
        hide={[
          // Below the coverage floor the Related to chip goes with the
          // destinations, so the page is either wholly records-aware or
          // wholly not (Claude Design, round 6b).
          ...(recordsEnabled ? [] : (["entities"] as const)),
          // A record location is the record's documents wherever they
          // are; the picker searches the place it stands in; a plain
          // folder listing is the folder (Drive keeps Location to Home
          // and results); a places listing lists places, not documents.
          ...(state.view === "record" ||
          mode === "picker" ||
          placesGroup !== null ||
          (state.view === "all" && !hasActiveFilters(state))
            ? (["location"] as const)
            : []),
          ...(state.view === "locked" || state.view === "processing"
            ? (["status"] as const)
            : state.view === "mine"
              ? (["people", "source"] as const)
              : state.view === "record"
                ? (["entities"] as const)
                : state.view === "portals"
                  ? // Every row's uploader is a service, so People
                    // would only widen the listing (spec R18); inside
                    // a service, the service is the source (R21).
                    inService
                    ? (["people", "source"] as const)
                    : (["people"] as const)
                  : []),
          ...(chipSet === "picker"
            ? ([
                "people",
                "source",
                "uploaded",
                "documentDate",
                "status",
                ...(phoneSheet ? (["type"] as const) : []),
              ] as const)
            : []),
        ]}
        onOpenRecord={mode === "page" ? onOpenRecord : undefined}
        columns={documentColumns.data ?? []}
        columnKinds={columnKinds}
        sourceKeys={portals ? portalSourceKeys : undefined}
        sourceGate={sourceGate}
        leading={
          mode === "page" ? null : (
            <>
              {!phoneSheet && (
                <SearchField
                  key={searchGeneration}
                  value={state.q}
                  onChange={(q) => update({ q })}
                  // Flexes with the row's slack, 168 to 240 (Claude Design,
                  // round 9); "documents" is the surface's own name.
                  className="min-w-[168px] max-w-[240px] flex-1"
                  placeholder="Search"
                  compactClear={narrowPicker}
                  inputRef={searchFieldRef}
                />
              )}
              {viewToggles}
            </>
          )
        }
      />
    </ChipsRow>
  );
  // A sync's row in the history narrows the documents to what it brought
  // (Round 15, R49 to R51); clicking it again, or the heading's pill,
  // clears it. Applying it scrolls the Documents heading into view, since
  // the history sits below the listing; clearing leaves the page where it
  // is. The state holds a run only inside a service (R51).
  const selectedRun = state.run;
  const toggleRun = (picked: RunWindow) => {
    if (stateRef.current.run?.from === picked.from) {
      update({ run: null });
      return;
    }
    update({ run: picked });
    documentsSectionRef.current?.scrollIntoView({
      block: "start",
      behavior: "smooth",
    });
  };
  // Always folders by manager (R30; R58: titled "Documents" on both); the
  // heading carries the listing's total, the chips row's number (R32).
  const documentsBand = (
    <Section
      title="Documents"
      count={listingLoading ? null : total}
      sectionRef={documentsSectionRef}
      trailing={
        syncHistory &&
        selectedRun !== null && (
          <SyncPill run={selectedRun} onClear={() => update({ run: null })} />
        )
      }
    >
      {/* The chips narrow the documents, so they sit under the heading,
          above the table: on a service's page (Round 20, R69) and on the
          overview's Documents tab (#15170). */}
      {bandChips && filterChipsRow}
      <div className={compact ? "" : "px-4"}>{body}</div>
      {loadMore}
    </Section>
  );
  const listing = portals ? (
    <div className="flex flex-col pb-4 pt-2">
      {!inService && (
        <div className="px-4 pb-2">
          <RecordTabRow
            label="Investor Portals sections"
            tabs={PORTALS_TABS}
            active={portalsTab}
            onSelect={(key) =>
              setPortalsTab(key === "documents" ? "documents" : "portals")
            }
          />
        </div>
      )}
      {(inService || portalsTab === "portals") && (
        <PortalsOverview
          services={portalServices}
          inService={inService}
          service={portalService}
          onPickService={(service) => onOpenService?.(service)}
          loading={portalServicesLoading}
          compact={compact}
          error={portalServicesError}
        />
      )}
      {/* The service's when-collected sits under its title row; its page
          opens on Managers, a first section in its own wrapper. */}
      {inService && portalService !== null && (
        <div>
          <ServiceManagers
            service={portalService}
            onOpenRecord={onOpenRecord}
          />
        </div>
      )}
      {inService && portalService !== null ? (
        <>
          <SectionRule />
          {/* Each wrapper makes its section a first one (no top pad): the
              rule above it is the gap (R61). */}
          <div>{documentsBand}</div>
          {/* A sync's history is Finpilot staff's, never a client's
              (#15170). */}
          {syncHistory && (
            <>
              <SectionRule />
              <div>
                <PortalRunsSection
                  service={portalService}
                  compact={compact}
                  selectedRun={selectedRun}
                  onToggleRun={toggleRun}
                />
              </div>
            </>
          )}
        </>
      ) : (
        (inService || portalsTab === "documents") && documentsBand
      )}
    </div>
  ) : (
    body
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {mode === "page" && header}
      {pinnedTarget && createPortal(pinnedGroup, pinnedTarget)}
      {mode === "page" &&
        searchHost &&
        createPortal(
          <SearchField
            key={searchGeneration}
            value={state.q}
            onChange={(q) => update({ q })}
            className="w-full"
            inputRef={searchFieldRef}
          />,
          searchHost
        )}
      {/* The phone sheet: search first, full width, above one scrolling
          row of chips (Claude Design, round 5). */}
      {phoneSheet && onlyFileIds === undefined && (
        <div className="px-4 pt-3">
          <SearchField
            key={searchGeneration}
            value={state.q}
            onChange={(q) => update({ q })}
            className="w-full"
          />
        </div>
      )}
      {/* Under the Selected scope the picks are the listing and nothing
          narrows them: the row keeps the scope group alone (the way back),
          no field and no filter chips; the count stays pinned. */}
      {onlyFileIds !== undefined && selectedScope && (
        <ChipsRow
          phoneSheet={phoneSheet}
          strip={mode === "picker"}
          trailing={pinnedTarget ? null : pinnedGroup}
        >
          {viewToggles}
        </ChipsRow>
      )}
      {chipsAbove && filterChipsRow}
      {selectAllLine && (
        <div
          data-testid="select-all-line"
          className="flex items-center gap-2 border-b border-[var(--fp-border)] bg-[var(--fp-accent-soft)] px-4 py-2 text-[12.5px] text-[var(--fp-ink-2)]"
        >
          <span>{selectAllLine.text}</span>
          {selectAllLine.action && (
            <button
              type="button"
              onClick={selectAllLine.action.run}
              className="font-medium text-[var(--fp-ink)] hover:underline"
            >
              {selectAllLine.action.label}
            </button>
          )}
        </div>
      )}
      <div ref={frameRef} className="min-h-[10rem] flex-1 overflow-auto">
        {/* A record hit is an answer, so it is rendered as one: a full-width row
            in the document rows' own rhythm, directly above the table header,
            which becomes the divider between the two kinds of answer. The pill
            it replaces was the same register the table uses for Related to
            cells — per-row attribute data — which told the eye that the
            highest-value hit on the page was a property of something else
            (Claude Design, round 7c). No caption: "RECORDS" above a single
            result was the smallest type on the page announcing the biggest
            thing on it. */}
        {suggestions.length > 0 && onOpenRecord && (
          <ul data-testid="record-suggestions" className="list-none">
            {suggestions.map((summary) => (
              <li key={summary.publicId}>
                <button
                  type="button"
                  onClick={() =>
                    onOpenRecord({
                      kind: summary.kind,
                      publicId: summary.publicId,
                      name: summary.name,
                    })
                  }
                  className={`flex min-h-[50px] w-full items-center gap-2.5 pr-3 text-left hover:bg-[var(--fp-hover)] ${
                    selection ? "pl-[52px]" : "pl-3"
                  }`}
                >
                  <EntityIcon
                    kind={recordIconKind(summary.kind)}
                    size={20}
                    aria-hidden
                    className="shrink-0 text-[var(--fp-ink)]"
                  />
                  {/* The name truncates and the count never does: the count
                        is the decision — it says the destination has content —
                        and the tail of a fund name is not. */}
                  <span
                    className={
                      compact
                        ? "flex min-w-0 flex-col"
                        : "flex min-w-0 flex-1 items-baseline gap-2"
                    }
                  >
                    <span className="min-w-0 truncate text-[13px] font-medium text-[var(--fp-ink)]">
                      {summary.name}
                    </span>
                    <span className="shrink-0 text-[12.5px] text-[var(--fp-ink-3)]">
                      {/* The count says what it counts. A bare "6" beside a
                          kind reads as a rank, an ID or a rating before it
                          reads as a number of documents (Hunter, round 7g);
                          and it is said only when worth reading (round 19). */}
                      {recordMetaLabel(summary.kind, summary.documentCount)}
                    </span>
                  </span>
                  {/* In the picker a record is a place, with a folder row's
                      affordance: the whole row navigates and a chevron says
                      so; its tick cell stays blank rather than disabled. */}
                  {mode === "picker" && (
                    <CaretRightIcon
                      data-testid="record-chevron"
                      size={14}
                      aria-hidden
                      className="ml-auto shrink-0 text-[var(--fp-ink-3)]"
                    />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {/* The body is the only thing that moves. The key is the kind of body,
            not the query, so the animation plays once on the crossing and the
            rows update in place while the reader keeps typing. */}
        <div
          key={bodyKind}
          className={crossed ? "fp-region-enter" : undefined}
          data-testid="documents-body"
        >
          {appsAboveListing && places && (
            <PlaceRows
              places={places}
              group="apps"
              onOpenPlace={openPlace}
              compact
            />
          )}
          {/* The phone's door to the portals view (spec R17): the column
              is gone below the line, so the row sits with the connected
              apps above the root listing. */}
          {portalsAboveListing && onOpenPortals && (
            <ul className="m-0 list-none p-0">
              <li className="border-b border-[var(--fp-border)]">
                <button
                  type="button"
                  aria-label="Investor Portals"
                  onClick={onOpenPortals}
                  className="group flex min-h-[48px] w-full items-center gap-2.5 px-4 py-2 text-left text-[13.5px] text-[var(--fp-ink)] hover:bg-[var(--fp-hover-row)]"
                >
                  <GlobeIcon
                    size={16}
                    aria-hidden
                    className="shrink-0 text-[var(--fp-ink-3)]"
                  />
                  <span className="min-w-0 flex-1 truncate">
                    Investor Portals
                  </span>
                  <span className="inline-flex shrink-0 items-center text-[var(--fp-ink-3)] group-hover:text-[var(--fp-ink)]">
                    <CaretRightIcon size={12} weight="bold" aria-hidden />
                  </span>
                </button>
              </li>
            </ul>
          )}
          {listing}
        </div>
        {/* The next page loads on scroll (Hunter, round 7d). A sentinel at the
            foot of the listing fetches when it comes within a screen of the
            viewport, so a long listing reads continuously; the row that says
            "Loading" is the only thing left to see. */}
        {!portals && loadMore}
      </div>
    </div>
  );
};

export default DocumentsBrowser;
